// Combat magic: a mana pool, spells learned from tomes and cast at the cursor with Q E R F, and
// wands/staves that boost spells and fire a missile as their basic attack. Blood Lance is the
// bridge to Blood Magic: it costs life essence (or HP) instead of mana. Data-driven; tune here.
import type { ItemId } from './items.ts';

export type SpellId = 'arcane_bolt' | 'renew' | 'ward' | 'fire_nova' | 'meteor' | 'blood_lance' | 'soul_siphon' | 'dragonfire';

export type SpellDef = {
  name: string;
  /** Mana cost; blood spells use `lp` instead (essence first, then HP, like sigils). */
  mana: number;
  lp?: number;
  cooldown: number;
  shape: 'bolt' | 'nova' | 'self' | 'target';
  dmg?: number;
  heal?: number;
  radius?: number; // tiles
  range?: number; // tiles
  tome: ItemId;
  text: string;
  /** Icon colour on the spell bar. */
  color: string;
  /** Bolts: heal the caster this share of the damage dealt (Soul Siphon). */
  siphon?: number;
};

export const SPELLS: Record<SpellId, SpellDef> = {
  arcane_bolt: { name: 'Arcane Bolt', mana: 8, cooldown: 0.45, shape: 'bolt', dmg: 16, range: 9, tome: 'tome_arcane_bolt', color: '#6aa8ff', text: 'A bolt of force where you aim (16 damage).' },
  renew: { name: 'Renew', mana: 20, cooldown: 5, shape: 'self', heal: 30, tome: 'tome_renew', color: '#6ad07a', text: 'Heal 30 health plus 10% of your maximum.' },
  ward: { name: 'Ward', mana: 25, cooldown: 14, shape: 'self', tome: 'tome_ward', color: '#e8e8ff', text: 'A shield that soaks up 40 damage for 12 seconds.' },
  fire_nova: { name: 'Fire Nova', mana: 22, cooldown: 4, shape: 'nova', dmg: 22, radius: 2.6, tome: 'tome_fire_nova', color: '#ff8a3a', text: 'Fire bursts out around you (22 damage to everything close).' },
  meteor: { name: 'Meteor', mana: 35, cooldown: 8, shape: 'target', dmg: 48, radius: 1.8, range: 8, tome: 'tome_meteor', color: '#ffb040', text: 'A meteor falls where you aim a moment later (48 damage in an area).' },
  // Late tomes (stage 8): only the realm bosses drop these.
  soul_siphon: { name: 'Soul Siphon', mana: 18, cooldown: 1.0, shape: 'bolt', dmg: 30, range: 9, siphon: 0.5, tome: 'tome_soul_siphon', color: '#60e0a0', text: 'A bolt that drinks a soul (30 damage); half of it heals you.' },
  dragonfire: { name: 'Dragonfire', mana: 40, cooldown: 3, shape: 'bolt', dmg: 45, radius: 1.8, range: 10, tome: 'tome_dragonfire', color: '#ff6a2a', text: 'A fireball that bursts where it hits (45 damage to everything nearby).' },
  blood_lance: { name: 'Blood Lance', mana: 0, lp: 120, cooldown: 1.2, shape: 'bolt', dmg: 42, range: 10, tome: 'tome_blood_lance', color: '#e0303a', text: 'A heavy bolt of blood (42 damage). Costs 120 life essence, or health if you have none.' },
};

export const isSpellId = (v: unknown): v is SpellId => typeof v === 'string' && Object.hasOwn(SPELLS, v);

export const SPELL_KEYS = ['Q', 'E', 'R', 'F'];
export const SPELL_SLOTS = SPELL_KEYS.length;

export const MANA = { base: 50, regen: 2 }; // per second
export const WARD = { absorb: 40, secs: 12 };
export const METEOR_DELAY = 0.9;
export const SPELL_BOLT_SPEED = 620;

/** A wand or staff's basic attack: a missile at range instead of a swing. */
export const WAND = { range: 6 * 48, speed: 700 };
